import React, { useState, useEffect } from "react";
import { authFetch, getToken } from "../pages/Auth";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  COLORS, CHART_COLORS, commonAxisStyle, commonGridStyle,
  tooltipStyle, cardStyle, chartHeaderStyle,
} from "../chartTheme";

const API_BASE = process.env.REACT_APP_API_URL || "https://pathriva-api-93474716090.us-central1.run.app";

// ═══════════════════════════════════════════════════════════
// DATA HOOK
// ═══════════════════════════════════════════════════════════

function useApi(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authFetch(path)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);

  return { data, loading, error };
}

// ═══════════════════════════════════════════════════════════
// SHARED UI
// ═══════════════════════════════════════════════════════════

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%",
        border: `3px solid ${COLORS.border}`,
        borderTopColor: COLORS.primary,
        animation: "spin 0.8s linear infinite",
      }} />
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div style={{ textAlign: "center", padding: 30 }}>
      <div style={{ color: COLORS.danger, fontSize: 12 }}>{message}</div>
    </div>
  );
}

function KPICard({ label, value, sub, color }) {
  return (
    <div style={{
      ...cardStyle,
      position: "relative",
      overflow: "hidden",
      flex: "1 1 200px",
      minWidth: 180,
    }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: color || COLORS.primary,
        borderRadius: "10px 10px 0 0",
      }} />
      <div style={{
        fontSize: 10, color: COLORS.muted, letterSpacing: 1,
        textTransform: "uppercase", marginBottom: 8,
      }}>{label}</div>
      <div style={{
        fontSize: 28, fontWeight: 800,
        color: color || COLORS.text, lineHeight: 1, marginBottom: 4,
      }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: COLORS.textDim }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, loading: isLoading, error: err }) {
  return (
    <div style={cardStyle}>
      <div style={{ marginBottom: 16 }}>
        <div style={chartHeaderStyle}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 10, color: COLORS.muted, fontFamily: "monospace", letterSpacing: 1 }}>
            {subtitle}
          </div>
        )}
      </div>
      {isLoading ? <Spinner /> : err ? <ErrorBox message={err} /> : children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// GAUGE COMPONENT
// ═══════════════════════════════════════════════════════════

function AdherenceGauge({ value }) {
  const pct = Math.min(100, Math.max(0, value || 0));
  const angle = (pct / 100) * 180;
  const rad = (angle * Math.PI) / 180;
  const cx = 100, cy = 90, r = 70;
  const x = cx - r * Math.cos(rad);
  const y = cy - r * Math.sin(rad);

  let color = COLORS.danger;
  if (pct >= 80) color = COLORS.success;
  else if (pct >= 60) color = COLORS.warning;

  return (
    <div style={{ textAlign: "center" }}>
      <svg viewBox="0 0 200 110" style={{ width: 220, height: 120 }}>
        <path d={`M 30 90 A 70 70 0 0 1 170 90`} fill="none" stroke={COLORS.border} strokeWidth="12" strokeLinecap="round" />
        <path
          d={`M 30 90 A 70 70 0 ${angle > 180 ? 1 : 0} 1 ${x} ${y}`}
          fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
        />
        <text x="100" y="80" textAnchor="middle" fill={color} fontSize="28" fontWeight="800">{pct}%</text>
        <text x="100" y="100" textAnchor="middle" fill={COLORS.muted} fontSize="10">PDC</text>
      </svg>
      <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>
        Industry typical: 50-65%
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

export default function EmployerDashboard() {
  const { data: overviewData, loading: oLoading, error: oError } = useApi("/api/employer/overview");
  const { data: roiData, loading: rLoading, error: rError } = useApi("/api/employer/roi");
  const { data: trendsData, loading: tLoading, error: tError } = useApi("/api/employer/trends");
  const { data: benchData, loading: bLoading, error: bError } = useApi("/api/employer/benchmarks");
  const [downloading, setDownloading] = useState(false);

  const overview = overviewData?.overview || {};
  const roi = roiData?.roi || {};
  const trends = (trendsData?.trends || []).map((t) => ({
    ...t,
    month: t.month ? t.month.substring(0, 7) : "",
  }));
  const benchmarks = benchData?.benchmarks || [];

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/api/employer/outcomes-report`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pathriva_outcomes_report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Failed to download report: " + e.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.text }}>Program Outcomes</div>
          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
            Aggregate, de-identified program data
          </div>
        </div>
        <button
          onClick={handleDownload}
          disabled={downloading}
          style={{
            padding: "10px 24px",
            background: downloading ? COLORS.border : "linear-gradient(135deg, #00e5ff, #7c3aed)",
            border: "none",
            borderRadius: 8,
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            cursor: downloading ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            letterSpacing: 0.5,
          }}
        >
          {downloading ? "Generating..." : "Download Report (PDF)"}
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        {oLoading ? <Spinner /> : oError ? <ErrorBox message={oError} /> : (
          <>
            <KPICard
              label="Total Enrolled"
              value={overview.total_patients?.toLocaleString() || "0"}
              sub={`${overview.active_patients?.toLocaleString() || "0"} active`}
              color={COLORS.primary}
            />
            <KPICard
              label="Retention Rate"
              value={`${overview.retention_rate_pct || 0}%`}
              sub={`Avg ${overview.avg_program_duration_months || 0} months`}
              color={COLORS.success}
            />
            <KPICard
              label="Avg Weight Loss"
              value={`${overview.avg_weight_loss_pct || 0}%`}
              sub={`BMI reduction: ${overview.avg_bmi_reduction || 0}`}
              color={COLORS.secondary}
            />
            <KPICard
              label="Estimated Annual Savings"
              value={`$${(roi.estimated_annual_savings || 0).toLocaleString()}`}
              sub="Based on retention + deprescribing"
              color={COLORS.warning}
            />
          </>
        )}
      </div>

      {/* Charts Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {/* Retention Trend */}
        <ChartCard title="Enrollment & Retention Trend" subtitle="MONTHLY | LAST 12 MONTHS" loading={tLoading} error={tError}>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trends}>
              <defs>
                <linearGradient id="empActive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="empEnrolled" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...commonGridStyle} />
              <XAxis dataKey="month" {...commonAxisStyle} />
              <YAxis {...commonAxisStyle} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 10, color: COLORS.muted }} />
              <Area type="monotone" dataKey="active_count" name="Active" stroke={COLORS.primary} fill="url(#empActive)" strokeWidth={2} />
              <Area type="monotone" dataKey="new_enrollments" name="New Enrollments" stroke={COLORS.secondary} fill="url(#empEnrolled)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Adherence Gauge */}
        <ChartCard title="Medication Adherence (PDC)" subtitle="PROPORTION OF DAYS COVERED" loading={oLoading} error={oError}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
            <AdherenceGauge value={overview.avg_pdc_pct} />
            <div style={{ marginTop: 16, fontSize: 11, color: COLORS.textDim, textAlign: "center" }}>
              PDC measures the percentage of days patients have medication available.
              <br />Above 80% is considered adherent.
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Weight Trend */}
      <div style={{ marginBottom: 24 }}>
        <ChartCard title="Average Weight Trend" subtitle="MONTHLY AGGREGATE | LBS" loading={tLoading} error={tError}>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trends.filter((t) => t.avg_weight_lbs)}>
              <defs>
                <linearGradient id="empWeight" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS[2]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={CHART_COLORS[2]} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...commonGridStyle} />
              <XAxis dataKey="month" {...commonAxisStyle} />
              <YAxis {...commonAxisStyle} domain={["dataMin - 10", "dataMax + 5"]} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="avg_weight_lbs" name="Avg Weight (lbs)" stroke={CHART_COLORS[2]} fill="url(#empWeight)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Benchmarks + ROI */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {/* Benchmark Comparison */}
        <ChartCard title="vs. Published Clinical Trials" subtitle="PROGRAM PERFORMANCE" loading={bLoading} error={bError}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Metric", "This Program", "Benchmark", "Diff"].map((h) => (
                    <th key={h} style={{
                      textAlign: "left", padding: "10px 12px", borderBottom: `1px solid ${COLORS.border}`,
                      color: COLORS.muted, fontSize: 10, textTransform: "uppercase", letterSpacing: 1,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {benchmarks.map((b, i) => {
                  const diff = typeof b.difference === "number" ? b.difference : 0;
                  const diffColor = diff >= 0 ? COLORS.success : COLORS.danger;
                  const diffSign = diff >= 0 ? "+" : "";
                  return (
                    <tr key={i}>
                      <td style={{ padding: "10px 12px", borderBottom: `1px solid ${COLORS.border}`, color: COLORS.text, fontWeight: 600 }}>
                        {b.metric}
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: `1px solid ${COLORS.border}`, color: COLORS.primary, fontWeight: 700 }}>
                        {b.program_value}{b.program_unit === "%" ? "%" : ` ${b.program_unit}`}
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: `1px solid ${COLORS.border}`, color: COLORS.textDim }}>
                        {b.benchmark_value}{typeof b.benchmark_value === "number" ? "%" : ""} <span style={{ fontSize: 9, color: COLORS.muted }}>({b.benchmark_name})</span>
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: `1px solid ${COLORS.border}`, color: diffColor, fontWeight: 700 }}>
                        {diffSign}{diff} pts
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {benchmarks.length > 0 && (
            <div style={{ fontSize: 9, color: COLORS.muted, marginTop: 12 }}>
              Sources: {benchmarks.map((b) => b.source).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join("; ")}
            </div>
          )}
        </ChartCard>

        {/* ROI Detail */}
        <ChartCard title="Return on Investment" subtitle="CONSERVATIVE ESTIMATES" loading={rLoading} error={rError}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{
              background: "rgba(0,229,255,0.05)", border: `1px solid rgba(0,229,255,0.2)`,
              borderRadius: 10, padding: 20, textAlign: "center",
            }}>
              <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                Estimated Annual Savings
              </div>
              <div style={{ fontSize: 36, fontWeight: 800, color: COLORS.primary }}>
                ${(roi.estimated_annual_savings || 0).toLocaleString()}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ background: COLORS.bgDark, borderRadius: 8, padding: 14, textAlign: "center" }}>
                <div style={{ fontSize: 9, color: COLORS.muted, textTransform: "uppercase", marginBottom: 4 }}>
                  Cost Avoided
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.success }}>
                  ${(roi.cost_avoided_from_interventions || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2 }}>from retained patients</div>
              </div>
              <div style={{ background: COLORS.bgDark, borderRadius: 8, padding: 14, textAlign: "center" }}>
                <div style={{ fontSize: 9, color: COLORS.muted, textTransform: "uppercase", marginBottom: 4 }}>
                  Deprescribing Savings
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.success }}>
                  ${(roi.deprescribing_savings || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2 }}>potential medication savings</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ background: COLORS.bgDark, borderRadius: 8, padding: 14, textAlign: "center" }}>
                <div style={{ fontSize: 9, color: COLORS.muted, textTransform: "uppercase", marginBottom: 4 }}>
                  Cost / Patient / Month
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>
                  ${(roi.cost_per_patient_per_month || 0).toLocaleString()}
                </div>
              </div>
              <div style={{ background: COLORS.bgDark, borderRadius: 8, padding: 14, textAlign: "center" }}>
                <div style={{ fontSize: 9, color: COLORS.muted, textTransform: "uppercase", marginBottom: 4 }}>
                  vs STEP 1 Retention
                </div>
                <div style={{
                  fontSize: 18, fontWeight: 700,
                  color: (roi.retention_vs_step1_pct_pts || 0) >= 0 ? COLORS.success : COLORS.danger,
                }}>
                  {(roi.retention_vs_step1_pct_pts || 0) >= 0 ? "+" : ""}{roi.retention_vs_step1_pct_pts || 0} pts
                </div>
              </div>
            </div>

            <div style={{ fontSize: 9, color: COLORS.muted, textAlign: "center", fontStyle: "italic" }}>
              {roiData?.methodology || ""}
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Footer disclaimer */}
      <div style={{
        ...cardStyle,
        background: "rgba(124,58,237,0.05)",
        border: `1px solid rgba(124,58,237,0.15)`,
        textAlign: "center",
        fontSize: 11,
        color: COLORS.textDim,
        lineHeight: 1.6,
      }}>
        All data shown is aggregated and de-identified. No individual patient information is included.
        <br />
        Savings estimates use conservative assumptions based on published clinical trial data and industry cost averages.
      </div>
    </div>
  );
}
